import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Loading, TabButton } from '@ui-elements';
import {
  useRecoilState,
  useRecoilValueLoadable,
  useRecoilRefresher_UNSTABLE,
} from 'recoil';
import {
  ModelAccessoriesPage,
  ModelAccessoryPacksQuery,
  ModelAccessoriesQuery,
  AccessoryRouteBreadcrumbs,
} from '../../store';
import { AccessoryPacksCarousel } from '../accessory-packs-carousel';
import { AccessoryCard } from '../accessory-card';
import { Accessory, AccessoryPack } from '~/common/models';
import styles from './index.module.scss';
import cn from 'classnames';

interface GroupAccessories {
  accessoryCategoryName: string;
  items: Accessory[];
}

export const AccessoriesMainPage = React.memo(() => {
  const [state] = useRecoilState(ModelAccessoriesPage);
  const [_, setBreadcrumbs] = useRecoilState(AccessoryRouteBreadcrumbs);
  const accessoryPacksLoadable = useRecoilValueLoadable(
    ModelAccessoryPacksQuery({
      pimId: state.pimIdentifier,
      showAccessoryGrouping: state.showAccessoryGrouping,
    })
  );
  const accessoriesLoadable = useRecoilValueLoadable(
    ModelAccessoriesQuery({
      pimId: state.pimIdentifier,
      showAccessoryGrouping: state.showAccessoryGrouping,
    })
  );

  const refreshAccessoryPacks = useRecoilRefresher_UNSTABLE(
    ModelAccessoryPacksQuery({
      pimId: state.pimIdentifier,
      showAccessoryGrouping: state.showAccessoryGrouping,
    })
  );
  const refreshAccessories = useRecoilRefresher_UNSTABLE(
    ModelAccessoriesQuery({
      pimId: state.pimIdentifier,
      showAccessoryGrouping: state.showAccessoryGrouping,
    })
  );

  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const [selectedMarketingCategory, setSelectedMarketingCategory] = useState<
    string | null
  >(null);

  const accessories =
    accessoriesLoadable.state === 'hasValue'
      ? accessoriesLoadable.getValue()
      : [];
  const accessoryPacks =
    accessoryPacksLoadable.state === 'hasValue'
      ? accessoryPacksLoadable.getValue()
      : [];

  useEffect(() => setBreadcrumbs([]), []);

  const uniqueMarketingCategories = useMemo(() => {
    return [
      ...new Set([
        ...(accessories || []).map((variant) =>
          (variant.variantMarketingCategory || '').trim()
        ),
        ...(accessoryPacks || []).map((variant) =>
          (variant.variantMarketingCategory || '').trim()
        ),
      ]),
    ].sort((a, b) => a.localeCompare(b));
  }, [accessories, accessoryPacks]);

  const marketingCategories = useMemo(() => {
    if (state.showAccessoryGrouping) {
      return state.marketingCategoryOrder?.length
        ? state.marketingCategoryOrder?.map((x) => x.trim())
        : uniqueMarketingCategories;
    }
    return [];
  }, [
    state.showAccessoryGrouping,
    state.marketingCategoryOrder,
    uniqueMarketingCategories,
  ]);

  const groupByDistinctValue = useCallback((arr: Accessory[]) => {
    const distinctValues = [
      ...new Set(arr.map((item) => item.accessoryCategoryName)),
    ];
    const groupedArray = distinctValues.map((value) => {
      return {
        accessoryCategoryName: value,
        items: arr.filter((item) => item.accessoryCategoryName === value),
      } as GroupAccessories;
    });
    return groupedArray;
  }, []);

  const groupAccessories = useMemo(() => {
    const allAccessories = (accessories || []).filter(
      (a) =>
        (a.variantMarketingCategory || '').trim() ===
          selectedMarketingCategory || !selectedMarketingCategory
    );
    const groupCategories = groupByDistinctValue(allAccessories);

    return [
      { accessoryCategoryName: 'All', items: allAccessories },
      ...groupCategories,
    ].filter((x) => x.accessoryCategoryName !== 'After Market');
  }, [accessories, selectedMarketingCategory, groupByDistinctValue]);

  useEffect(() => {
    if (
      selectedCategory !== 'All' &&
      !groupAccessories
        .map((gc) => gc.accessoryCategoryName)
        .includes(selectedCategory)
    ) {
      setSelectedCategory('All');
    }
  }, [groupAccessories, selectedCategory]);

  useEffect(() => {
    if (marketingCategories.length > 0) {
      setSelectedMarketingCategory(marketingCategories[0]);
    }
  }, [marketingCategories]);

  const handleSelectCategory = (groupAcc: GroupAccessories) => {
    setSelectedCategory(groupAcc.accessoryCategoryName);
  };

  const getAccessories = useCallback(
    (category: string) => {
      const allAccessories = (accessories || []).filter(
        (a) =>
          (a.variantMarketingCategory || '').trim() ===
            selectedMarketingCategory || !selectedMarketingCategory
      );
      if (category === 'All') return allAccessories;

      const groupAccByCategory = groupByDistinctValue(allAccessories);
      const foundData = groupAccByCategory?.find((x) => {
        return x.accessoryCategoryName === category;
      });
      return foundData?.items || [];
    },
    [accessories, selectedMarketingCategory, groupByDistinctValue]
  );

  return (
    <div className={styles.mainpage}>
      <div
        className={styles.accessoriesHeading}
        dangerouslySetInnerHTML={{ __html: state.heading }}
      />

      <div className={styles.accessoryPacksContent}>
        <div>
          <div
            className={styles.accessoriesSubHeading}
            dangerouslySetInnerHTML={{ __html: state.subHeading }}
          />

          {!!marketingCategories?.length && (
            <div className={styles.marketingCategoryWrapper}>
              <div className={styles.marketingCategoryTabs}>
                {marketingCategories.map((mc) => (
                  <div
                    key={mc}
                    onClick={() => setSelectedMarketingCategory(mc)}
                    className={cn(
                      styles.marketingCategoryItem,
                      mc === selectedMarketingCategory &&
                        styles.marketingCategoryActive
                    )}
                  >
                    {mc}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!groupAccessories?.length && (
            <div className={styles.accessoriesTabCategory}>
              <div>
                {groupAccessories?.map((ac) => (
                  <TabButton
                    key={ac.accessoryCategoryName}
                    className={styles.tabCategory}
                    value={ac.accessoryCategoryName}
                    onClick={() => handleSelectCategory(ac)}
                    active={ac.accessoryCategoryName === selectedCategory}
                  />
                ))}
              </div>
            </div>
          )}

          <p className={styles.accessoryPackInnerTitle}>
            {state.accessoryPackTitleOverride || 'Accessory Packs'}
          </p>
          {accessoryPacksLoadable.state === 'loading' ? (
            <Loading height="500px" />
          ) : accessoryPacksLoadable.state === 'hasValue' ? (
            <AccessoryPacksCarousel
              accessoryPacks={accessoryPacks.filter(
                (a) =>
                  (a.variantMarketingCategory || '').trim() ===
                    selectedMarketingCategory || !selectedMarketingCategory
              )}
            />
          ) : (
            <div className={styles.accessoryLoadError}>
              <p>
                Sorry, we encountered an error while loading the accessory
                packs.
                <span onClick={refreshAccessoryPacks}>
                  Please click here to try again.
                </span>
              </p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.accessoriesContent}>
        <div className="container">
          <p className={styles.accessoryInnerTitle}>Individual Accessories</p>
          {accessoriesLoadable.state === 'loading' ? (
            <Loading height="500px" />
          ) : accessoriesLoadable.state === 'hasValue' ? (
            <div className={styles.accessoriesGrid}>
              {getAccessories(selectedCategory).map((acc, index) => (
                <AccessoryCard
                  key={acc.id}
                  delay={(index + 1) * 100}
                  {...acc}
                />
              ))}
            </div>
          ) : (
            <div className={styles.accessoryLoadError}>
              <p>
                Sorry, we encountered an error while loading the accessories.
                <span onClick={refreshAccessories}>
                  Please click here to try again.
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
